import React, { useCallback, useMemo } from 'react';
import { TableKeys } from '../../../models';
import useSelector from '../../../infrastructure/useSelector';
import { useDispatch } from 'react-redux';
import { setDisplayedNotification, setSearchNotifications, setTableState } from '../../../state/actions/notificationsList';
import AppTable from '../../AppTable/AppTable';
import { getAudiencesOptions } from '../../../helpers/audiences';
import { useAudiencesLists } from '../../Audiences/hooks/hooks';
import NoRulesEmptyStateImage from '../../../../assets/images/no-rules-found.png';
import EmptyState from '../../EmptyState/EmptyState';
import classes from './NotificationsTable.module.scss';
import notificationEmptyState from '../../../../assets/images/notification-empty-state.png';
import { useNotificationsCount, useNotificationsData, useNotificationsFilterFields } from '../hooks/notifications';
import { IAppTableProps, ITableData } from '../../AppTable/model';
import NotificationsTableNameTitle from './table-parts/NotificationsTableNameTitle/NotificationsTableNameTitle';
import { parseNotificationFiltersFields } from './helpers/parseNotificationFiltersFields';
import { useTranslation } from 'react-i18next';

const keyPrefix = 'components.notificationsList.notificationsTable.notificationsTable';

export interface INotificationTableProps extends Omit<IAppTableProps, 'onTableDataChange' | 'renderTopComponent' | 'isActiveRow' | 'searchKeys'> {
    tableKey: TableKeys;
}

const dataWithAudiences = (data: $TSFixMe, audiencesList: $TSFixMe) =>
    data?.map((row: $TSFixMe) => ({
        ...row,
        audiences: getAudiencesOptions(row.audiencesIds, audiencesList)
    }));

export enum TableDataAttributes {
    NOTIFICATIONS_LISTS_SIZES = 'data-notifications-lists-sizes'
}

export const NotificationsTable = ({
    'data-testid': tableDataTestid,
    tableName,
    tableKey,
    onRowClick,
    columns,
    environmentId,
    ...restProps
}: INotificationTableProps) => {
    const { t } = useTranslation('', { keyPrefix });

    const dispatch = useDispatch();
    const searchTerm = useSelector(state => state.notifications.searchTerm);
    const tableData = useSelector(state => state.notifications.tableStates[tableKey]);
    const notificationsCount = useNotificationsCount();
    const { data: notificationsData, loading } = useNotificationsData(tableKey);
    const serverFilterFields = useNotificationsFilterFields();

    const drawerNotificationId = useSelector(store => store.notifications.drawer?.data?.notificationId);
    const { audiencesList, archivedAudiencesList } = useAudiencesLists();

    const notificationsFiltersFields = useMemo(() => {
        const fields = serverFilterFields?.[tableKey];
        return parseNotificationFiltersFields(fields, [...(audiencesList || []), ...(archivedAudiencesList || [])]);
    }, [archivedAudiencesList, audiencesList, serverFilterFields, tableKey]);

    const data = useMemo(() => dataWithAudiences(notificationsData?.list, [...(audiencesList || []), ...(archivedAudiencesList || [])]), [
        notificationsData,
        audiencesList,
        archivedAudiencesList
    ]);

    const onTableDataChange = useCallback((data: Partial<ITableData>) => dispatch(setTableState(tableKey, data)), [dispatch, tableKey]);

    const defaultOnRowClick = useCallback(
        (event: any) => {
            dispatch(setDisplayedNotification(event.rowData));
        },
        [dispatch]
    );

    const onSearchTermChange = useCallback(
        (payload: string) => {
            dispatch(setSearchNotifications(payload));
        },
        [dispatch]
    );

    const emptyStateOnTab = (tableKey: TableKeys) => {
        return <EmptyState title={t('emptyStateOnTab', { tableKey })} image={notificationEmptyState} />;
    };

    const emptyRenderer = () => {
        if (notificationsCount?.[tableKey] === 0) {
            return emptyStateOnTab(tableKey);
        }

        if (searchTerm && searchTerm?.length > 0) {
            return (
                <EmptyState
                    title={t('emptySearch.title')}
                    description={t('emptySearch.description')}
                    actionText={t('emptySearch.actionText')}
                    image={NoRulesEmptyStateImage}
                    onClick={() => onSearchTermChange('')}
                />
            );
        }

        if (Object.values<string[]>(tableData.filters).some(values => values.length > 0)) {
            return (
                <EmptyState
                    title={t('emptyFilter.title')}
                    description={t('emptyFilter.description')}
                    actionText={t('emptyFilter.actionText')}
                    image={NoRulesEmptyStateImage}
                    onClick={() => onTableDataChange({ filters: {} })}
                />
            );
        }
        return null;
    };

    return (
        <div className={classes.root}>
            <AppTable
                data-testid={tableDataTestid}
                environmentId={environmentId}
                data={data}
                fetching={data && loading}
                count={notificationsData?.count}
                filtersFields={notificationsFiltersFields}
                tableName={<NotificationsTableNameTitle count={notificationsData?.count} subtitle={tableName!} />}
                searchTerm={searchTerm}
                searchKeys={['title', 'runtimeTitle']}
                onSearchTermChange={onSearchTermChange}
                tableData={tableData}
                onTableDataChange={onTableDataChange}
                isActiveRow={rowData => rowData.id === drawerNotificationId}
                onRowClick={onRowClick || defaultOnRowClick}
                columns={columns}
                emptyTableRenderer={emptyRenderer}
                {...restProps}
            />
        </div>
    );
};
